// PRO Drift Auditor -- institutional holdings + simulation-parameter form.
//
// Form values live in the persisted useProDriftFormStore (latest inputs survive
// navigation / refresh); delegates the network call upward via onSubmit.
// Adds the PRO knobs over the free tier: optional live drifted weight per
// holding, capital-gains tax, periodic cash inflow, rebalance cooldown and the
// Sharpe risk-free rate -- driven by polished range sliders and exact inputs.

import React, { useEffect, useMemo, useState } from 'react';
import { Gauge, Plus, RotateCcw, Sparkles, X } from 'lucide-react';
import { useShallow } from 'zustand/react/shallow';

import { useDataAwareLookback } from '../../../shared/hooks/useDataAwareLookback';
import { useDebounce } from '../../../shared/hooks/useDebounce';
import { SymbolAutocomplete } from '../../../shared/ui';
import { formatHistorySpan, toCoverageTickers } from '../../../shared/utils/lookback';
import { useProDriftCoverage } from '../api';
import {
    DEFAULT_HOLDINGS,
    DEFAULT_LOOKBACK_YEARS,
    FORM_BOUNDS,
    MAX_HOLDINGS,
    MAX_TICKER_LENGTH,
    MIN_HOLDINGS,
    useProDriftFormStore,
} from '../store/useProDriftFormStore';
import type { ProDriftAuditRequest } from '../types';

interface ProDriftAuditorFormProps {
    onSubmit: (data: ProDriftAuditRequest) => void;
    isPending: boolean;
    error: Error | null;
}

const TICKER_RE = /^[A-Z0-9^][A-Z0-9.\-=]{0,15}$/;
// Mirrors backend STANDARD_LOOKBACK_OPTIONS (lookback_years 1..20).
const STANDARD_LOOKBACK_OPTIONS: readonly number[] = [1, 2, 3, 5, 10, 15, 20];

const PRESETS: { label: string; holdings: readonly (readonly [string, number])[] }[] = [
    { label: 'Classic 60/40', holdings: DEFAULT_HOLDINGS },
    {
        label: 'Tech-Heavy',
        holdings: [['NVDA', 30], ['MSFT', 25], ['GOOGL', 20], ['AAPL', 15], ['AVGO', 10]],
    },
    {
        label: 'All-Weather',
        holdings: [['SPY', 30], ['TLT', 40], ['IEF', 15], ['GLD', 7.5], ['DBC', 7.5]],
    },
    { label: 'Global UCITS', holdings: [['VWCE.DE', 50], ['SXR8.DE', 30], ['AGGH', 20]] },
];

const usd = (v: number): string => `$${v.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

// '' (cleared / mid-typing) -> null; negatives clamp to 0. Never yields NaN.
const parseCashInput = (raw: string): number | null => {
    if (raw.trim() === '') return null;
    const v = Number(raw);
    return Number.isFinite(v) ? Math.max(0, v) : null;
};

const inputClass =
    'w-full px-3.5 py-3 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg text-base text-slate-900 dark:text-white placeholder:text-slate-400 placeholder:normal-case [color-scheme:light] dark:[color-scheme:dark] outline-none transition-colors focus:bg-white dark:focus:bg-slate-800 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/40';

const TICKER_PLACEHOLDERS = ['e.g. SPY', 'e.g. AGG', 'e.g. VWCE.DE', 'e.g. BTCUSD', 'e.g. GLD'];

// -- Slider atom ------------------------------------------------------------
interface SliderProps {
    label: string;
    value: number;
    min: number;
    max: number;
    step: number;
    format: (v: number) => string;
    onChange: (v: number) => void;
    accent?: string;
    hint?: string;
}

const Slider: React.FC<SliderProps> = ({
    label,
    value,
    min,
    max,
    step,
    format,
    onChange,
    accent = '#6366f1',
    hint,
}) => {
    const pct = ((value - min) / (max - min)) * 100;
    return (
        <label className="block">
            <div className="flex items-baseline justify-between gap-2">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {label}
                </span>
                <span className="text-sm font-black tabular-nums text-slate-900 dark:text-white">
                    {format(value)}
                </span>
            </div>
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                aria-label={label}
                className="mt-2 w-full appearance-none h-2 rounded-full cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400"
                style={{
                    background: `linear-gradient(to right, ${accent} 0%, ${accent} ${pct}%, rgba(148,163,184,0.3) ${pct}%, rgba(148,163,184,0.3) 100%)`,
                }}
            />
            {hint && <p className="mt-1 text-[11px] leading-snug text-slate-400">{hint}</p>}
        </label>
    );
};

export const ProDriftAuditorForm: React.FC<ProDriftAuditorFormProps> = ({
    onSubmit,
    isPending,
    error,
}) => {
    const {
        rows,
        initialCapital,
        savedLookbackYears,
        corridorPct,
        txCostBps,
        capGainsPct,
        monthlyCash,
        cooldownDays,
        riskFreePct,
        useLiveWeights,
        setField,
        updateRow,
        removeRow,
        addRow,
        loadPreset,
        reset,
    } = useProDriftFormStore(
        useShallow((s) => ({
            rows: s.rows,
            initialCapital: s.initialCapital,
            savedLookbackYears: s.lookbackYears,
            corridorPct: s.corridorPct,
            txCostBps: s.txCostBps,
            capGainsPct: s.capGainsPct,
            monthlyCash: s.monthlyCash,
            cooldownDays: s.cooldownDays,
            riskFreePct: s.riskFreePct,
            useLiveWeights: s.useLiveWeights,
            setField: s.setField,
            updateRow: s.updateRow,
            removeRow: s.removeRow,
            addRow: s.addRow,
            loadPreset: s.loadPreset,
            reset: s.reset,
        })),
    );
    const [localError, setLocalError] = useState<string | null>(null);

    const totalWeight = useMemo(
        () => rows.reduce((sum, r) => sum + (Number.isFinite(r.weight) ? r.weight : 0), 0),
        [rows],
    );
    const totalLiveWeight = useMemo(
        () => rows.reduce((sum, r) => sum + (r.currentWeight && Number.isFinite(r.currentWeight) ? r.currentWeight : 0), 0),
        [rows],
    );

    // Probe the price store for how much overlapping history these tickers have
    // so the Lookback dropdown only offers windows we can actually serve.
    const validTickers = useMemo(
        () => toCoverageTickers(rows.map((r) => r.ticker), TICKER_RE),
        [rows],
    );
    const debouncedTickers = useDebounce(validTickers, 400);
    const { data: coverage } = useProDriftCoverage(debouncedTickers);
    const { lookbackYears, setLookbackYears, lookbackOptions } = useDataAwareLookback(
        STANDARD_LOOKBACK_OPTIONS,
        coverage,
        savedLookbackYears,
    );

    // Persist the data-aware clamp only once real coverage is known, so the
    // transient "no coverage yet" option list never overwrites a saved
    // max-available window (e.g. 7Y) during re-hydration.
    useEffect(() => {
        if (coverage) setField('lookbackYears', lookbackYears);
    }, [coverage, lookbackYears, setField]);

    const handleLookbackChange = (years: number) => {
        setLookbackYears(years);
        setField('lookbackYears', years);
    };

    const handleReset = () => {
        reset();
        setLookbackYears(DEFAULT_LOOKBACK_YEARS);
        setLocalError(null);
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const seen = new Set<string>();
        const holdings: { ticker: string; weight: number; currentWeight: number | null }[] = [];
        for (const row of rows) {
            const ticker = row.ticker.trim().toUpperCase();
            if (!ticker) continue;
            if (!TICKER_RE.test(ticker)) {
                setLocalError(`"${row.ticker}" is not a valid ticker symbol.`);
                return;
            }
            if (!(row.weight > 0)) {
                setLocalError(`Target weight for ${ticker} must be greater than 0.`);
                return;
            }
            if (seen.has(ticker)) {
                setLocalError(`Duplicate ticker ${ticker}. Combine its weight into one row.`);
                return;
            }
            seen.add(ticker);
            holdings.push({ ticker, weight: row.weight, currentWeight: row.currentWeight });
        }
        if (holdings.length < MIN_HOLDINGS || holdings.length > MAX_HOLDINGS) {
            setLocalError(`Please enter between ${MIN_HOLDINGS} and ${MAX_HOLDINGS} distinct holdings.`);
            return;
        }
        if (!(initialCapital >= FORM_BOUNDS.initialCapital.min && initialCapital <= FORM_BOUNDS.initialCapital.max)) {
            setLocalError(
                `Initial capital must be between ${usd(FORM_BOUNDS.initialCapital.min)} and ${usd(FORM_BOUNDS.initialCapital.max)}.`,
            );
            return;
        }
        const cashInflow = Math.max(0, monthlyCash ?? 0);
        if (cashInflow > FORM_BOUNDS.monthlyCash.max) {
            setLocalError(`Monthly cash inflow cannot exceed ${usd(FORM_BOUNDS.monthlyCash.max)}.`);
            return;
        }

        const total = holdings.reduce((s, h) => s + h.weight, 0);
        const liveProvided = useLiveWeights && holdings.every((h) => (h.currentWeight ?? 0) > 0);
        const liveTotal = holdings.reduce((s, h) => s + (h.currentWeight ?? 0), 0);

        setLocalError(null);
        onSubmit({
            holdings: holdings.map((h) => ({
                ticker: h.ticker,
                target_weight: h.weight / total,
                current_weight: liveProvided ? (h.currentWeight ?? 0) / liveTotal : null,
            })),
            initial_capital: initialCapital,
            lookback_years: lookbackYears,
            corridor_threshold: corridorPct / 100,
            transaction_cost_bps: txCostBps,
            capital_gains_tax_bps: capGainsPct * 100,
            monthly_cash_inflow: cashInflow,
            rebalance_cooldown_days: cooldownDays,
            risk_free_rate: riskFreePct / 100,
        });
    };

    const shownError = localError ?? error?.message ?? null;
    const isBalanced = Math.abs(totalWeight - 100) < 0.01;
    const isLiveBalanced = Math.abs(totalLiveWeight - 100) < 0.5;

    return (
        <form
            onSubmit={handleSubmit}
            className="bg-white dark:bg-slate-900/70 p-6 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 space-y-5"
        >
            <div className="flex items-center gap-2">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 text-white shadow-lg shadow-indigo-500/30">
                    <Sparkles className="h-5 w-5" />
                </span>
                <div className="flex-1">
                    <h2 className="text-base font-black text-slate-900 dark:text-white leading-tight">
                        Institutional Audit Inputs
                    </h2>
                    <p className="text-[11px] text-slate-500 dark:text-slate-400">
                        Tax-lot, cash-flow & regime-aware engine
                    </p>
                </div>
                <button
                    type="button"
                    onClick={handleReset}
                    title="Inputs are saved automatically. Reset restores the defaults."
                    className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-slate-500 dark:text-slate-400 hover:text-indigo-700 dark:hover:text-indigo-300 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors"
                >
                    <RotateCcw className="h-3.5 w-3.5" /> Reset
                </button>
            </div>

            <div>
                <p className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                    Quick-start examples
                </p>
                <div className="flex flex-wrap gap-2">
                    {PRESETS.map((preset) => (
                        <button
                            key={preset.label}
                            type="button"
                            onClick={() => loadPreset(preset.holdings)}
                            className="px-3 py-1.5 text-xs font-semibold rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 text-slate-700 dark:text-slate-200 hover:border-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
                        >
                            {preset.label}
                        </button>
                    ))}
                </div>
            </div>

            <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                    Target Allocation
                </label>
                <div className="flex gap-2 items-center px-1 mb-1.5">
                    <span className="flex-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                        Ticker
                    </span>
                    <span className="w-20 text-xs font-semibold uppercase tracking-wide text-slate-400 text-right">
                        Target %
                    </span>
                    {useLiveWeights && (
                        <span className="w-20 text-xs font-semibold uppercase tracking-wide text-indigo-400 text-right">
                            Live %
                        </span>
                    )}
                    <span className="w-9 shrink-0" aria-hidden="true"></span>
                </div>
                <div className="space-y-2.5">
                    {rows.map((row, index) => (
                        <div key={row.id} className="flex gap-2 items-center">
                            <SymbolAutocomplete
                                value={row.ticker}
                                onChange={(next) => updateRow(row.id, { ticker: next })}
                                accent="indigo"
                                placeholder={TICKER_PLACEHOLDERS[index % TICKER_PLACEHOLDERS.length]}
                                ariaLabel={`Ticker for holding ${index + 1}`}
                                maxLength={MAX_TICKER_LENGTH}
                                inputClassName={`${inputClass} font-semibold tracking-wide uppercase`}
                                listboxId={`pro-drift-symbols-${row.id}`}
                            />
                            <div className="relative w-20 shrink-0">
                                <input
                                    type="number"
                                    value={Number.isFinite(row.weight) ? row.weight : ''}
                                    onChange={(e) => updateRow(row.id, { weight: Number(e.target.value) })}
                                    min="0"
                                    step="any"
                                    inputMode="decimal"
                                    aria-label={`Target weight percent for holding ${index + 1}`}
                                    className={`${inputClass} px-2 text-right`}
                                />
                            </div>
                            {useLiveWeights && (
                                <div className="relative w-20 shrink-0">
                                    <input
                                        type="number"
                                        value={row.currentWeight ?? ''}
                                        onChange={(e) =>
                                            updateRow(row.id, {
                                                currentWeight:
                                                    e.target.value === '' ? null : Number(e.target.value),
                                            })
                                        }
                                        min="0"
                                        step="any"
                                        inputMode="decimal"
                                        placeholder="—"
                                        aria-label={`Live weight percent for holding ${index + 1}`}
                                        className={`${inputClass} px-2 text-right border-indigo-200 dark:border-indigo-800`}
                                    />
                                </div>
                            )}
                            <button
                                type="button"
                                onClick={() => removeRow(row.id)}
                                disabled={rows.length <= MIN_HOLDINGS}
                                aria-label={`Remove ${row.ticker || `holding ${index + 1}`}`}
                                className="shrink-0 w-9 h-11 flex items-center justify-center rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 disabled:opacity-30 transition-colors"
                            >
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                    ))}
                </div>
                <div className="flex items-center justify-between mt-3 gap-2 flex-wrap">
                    <button
                        type="button"
                        onClick={addRow}
                        disabled={rows.length >= MAX_HOLDINGS}
                        className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 disabled:opacity-40"
                    >
                        <Plus className="h-4 w-4" /> Add holding
                    </button>
                    <div className="flex items-center gap-2">
                        {useLiveWeights && (
                            <span
                                className={`text-xs font-bold px-2 py-1 rounded ${
                                    isLiveBalanced
                                        ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300'
                                        : 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'
                                }`}
                            >
                                Live: {totalLiveWeight.toFixed(1)}%
                            </span>
                        )}
                        <span
                            className={`text-xs font-bold px-2 py-1 rounded ${
                                isBalanced
                                    ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300'
                                    : 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'
                            }`}
                        >
                            Target: {totalWeight.toFixed(1)}%
                        </span>
                    </div>
                </div>
                <label className="mt-3 flex items-center gap-2 text-xs font-medium text-slate-600 dark:text-slate-300 cursor-pointer select-none">
                    <input
                        type="checkbox"
                        checked={useLiveWeights}
                        onChange={(e) => setField('useLiveWeights', e.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    I know my actual live drifted weights (sizes the blotter from my real book)
                </label>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1.5">
                        Initial Capital
                    </label>
                    <div className="relative">
                        <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm font-medium text-slate-400">
                            $
                        </span>
                        <input
                            type="number"
                            value={Number.isFinite(initialCapital) ? initialCapital : ''}
                            onChange={(e) => setField('initialCapital', Number(e.target.value))}
                            min={FORM_BOUNDS.initialCapital.min}
                            max={FORM_BOUNDS.initialCapital.max}
                            step="1000"
                            className={`${inputClass} pl-7`}
                        />
                    </div>
                </div>
                <div>
                    <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1.5">
                        Lookback
                    </label>
                    <select
                        value={lookbackYears}
                        onChange={(e) => handleLookbackChange(Number(e.target.value))}
                        className={`${inputClass} py-3`}
                    >
                        {lookbackOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </select>
                    {coverage && coverage.max_lookback_years > 0 && (
                        <p className="mt-1.5 text-[11px] text-slate-400">
                            {formatHistorySpan(coverage.max_lookback_years)} of shared history for these
                            tickers.
                        </p>
                    )}
                </div>
            </div>

            <div className="pt-1 border-t border-slate-100 dark:border-slate-800 space-y-4">
                <div className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 pt-3">
                    <Gauge className="h-4 w-4 text-indigo-500" /> Rebalancing & Friction
                </div>
                <Slider
                    label="Rebalance Corridor"
                    value={corridorPct}
                    {...FORM_BOUNDS.corridorPct}
                    format={(v) => `±${v}%`}
                    onChange={(v) => setField('corridorPct', v)}
                    accent="#6366f1"
                    hint="Relative drift band vs each holding's target weight."
                />
                <div className="grid grid-cols-2 gap-4">
                    <Slider
                        label="Transaction Cost"
                        value={txCostBps}
                        {...FORM_BOUNDS.txCostBps}
                        format={(v) => `${v} bps`}
                        onChange={(v) => setField('txCostBps', v)}
                        accent="#0ea5e9"
                        hint={`0–${FORM_BOUNDS.txCostBps.max} bps per trade (${(txCostBps / 100).toFixed(2)}%). UCITS / wide-spread venues often run 200–400 bps.`}
                    />
                    <Slider
                        label="Capital Gains Tax"
                        value={capGainsPct}
                        {...FORM_BOUNDS.capGainsPct}
                        format={(v) => `${v}%`}
                        onChange={(v) => setField('capGainsPct', v)}
                        accent="#f43f5e"
                        hint={`0–${FORM_BOUNDS.capGainsPct.max}% on realised gains — covers high-tax European regimes.`}
                    />
                </div>
                <div>
                    <label
                        htmlFor="pro-drift-monthly-cash"
                        className="flex items-baseline justify-between gap-2 mb-1.5"
                    >
                        <span className="text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                            Monthly Cash Inflow
                        </span>
                        <span className="text-sm font-black tabular-nums text-slate-900 dark:text-white">
                            {monthlyCash ? `${usd(monthlyCash)}/mo` : 'None'}
                        </span>
                    </label>
                    <div className="relative">
                        <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-sm font-medium text-slate-400">
                            $
                        </span>
                        <input
                            id="pro-drift-monthly-cash"
                            type="number"
                            value={monthlyCash ?? ''}
                            onChange={(e) => setField('monthlyCash', parseCashInput(e.target.value))}
                            min={FORM_BOUNDS.monthlyCash.min}
                            max={FORM_BOUNDS.monthlyCash.max}
                            step="any"
                            inputMode="decimal"
                            placeholder="e.g. 2500"
                            className={`${inputClass} pl-7 pr-12 tabular-nums`}
                        />
                        <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs font-medium text-slate-400">
                            /mo
                        </span>
                    </div>
                    <p className="mt-1 text-[11px] leading-snug text-slate-400">
                        Routed to underweight holdings first — repairs drift without selling winners. Leave empty for none.
                    </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <Slider
                        label="Rebalance Cooldown"
                        value={cooldownDays}
                        {...FORM_BOUNDS.cooldownDays}
                        format={(v) => `${v}d`}
                        onChange={(v) => setField('cooldownDays', v)}
                        accent="#8b5cf6"
                    />
                    <Slider
                        label="Risk-Free Rate"
                        value={riskFreePct}
                        {...FORM_BOUNDS.riskFreePct}
                        format={(v) => `${v}%`}
                        onChange={(v) => setField('riskFreePct', v)}
                        accent="#64748b"
                    />
                </div>
            </div>

            <button
                type="submit"
                disabled={isPending}
                className="w-full mt-2 bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 text-white font-bold py-3.5 rounded-xl transition-all disabled:opacity-70 flex justify-center items-center gap-2 shadow-lg shadow-indigo-500/25"
            >
                {isPending ? (
                    <>
                        <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                        Running Institutional Audit…
                    </>
                ) : (
                    <>
                        <Sparkles className="h-4 w-4" /> Run PRO Audit
                    </>
                )}
            </button>

            {shownError && (
                <div className="p-3 bg-rose-50 dark:bg-rose-950/40 text-rose-600 dark:text-rose-300 rounded-lg text-sm border border-rose-100 dark:border-rose-900 mt-2 font-medium">
                    {shownError}
                </div>
            )}
        </form>
    );
};
